import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  deleteQuestionType,
  fetchAllQuestionTypes,
  fetchMappedSubjects,
  mapTypeToSubjects,
} from "../redux/boardQuestionSlice";
import {
  fetchClassesByBoardMedium,
  fetchLinkedSubjects,
} from "../redux/chapterSlice";
import { fetchMediums } from "../redux/mediumSlice";
import Table from "../../Table";
import Popup from "../../../Popup";
import showToast from "../../../utils/toast";
import { Toaster } from "react-hot-toast";
import API_BASE_URL from "../../config";
import { setEditingCategory, setShowForm } from "../../categories/redux/categorySlice";
import CategoryForm from "../../categories/CategoryForm";

const QuestionTypes = () => {
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const [selectedType, setSelectedType] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Popup form state
  const [selectedMediumId, setSelectedMediumId] = useState("");
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  const {
    allQuestionTypes,
    allQuestionTypesLoading,
    mappedSubjects,
    mappedSubjectsLoading,
    mappingTypeToSubjects,
    error,
  } = useSelector((state) => state.boardQuestion);

  // Get mediums, classes, subjects from Redux
  const mediums = useSelector((state) => state.mediums.list);
  const mediumsLoading = useSelector((state) => state.mediums.loading);
  const { classes, subjects, classesLoading, subjectsLoading } = useSelector(
    (state) => state.boardChapters
  );

  const { categories, loading, showForm, linkForm, editingCategory } = useSelector(
    (state) => state.category
  );
  const handleCreateNew = () => {
    dispatch(setEditingCategory(null));
    dispatch(setShowForm(true));
  };
  const handleFormCancel = () => {
    dispatch(setEditingCategory(null));
    dispatch(setShowForm(false));
  };
  const handleEditClick = (item) => {
    dispatch(setEditingCategory(item));
    dispatch(setShowForm(true));
  };
  const handleStatusToggle = async (typeId, currentStatus) => {
    const isCurrentlyActive = currentStatus === 1;
    const newStatus = isCurrentlyActive ? 0 : 1;
    const label = isCurrentlyActive ? "unpublish" : "publish";

    if (!window.confirm(`Are you sure you want to ${label} this question type?`)) return;

    const toastId = showToast.loading(
      isCurrentlyActive ? "Unpublishing..." : "Publishing..."
    );

    try {
      await fetch(`${API_BASE_URL}/classes/update-status/${typeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: newStatus, updated_by: 1 }),
      });

      showToast.dismiss(toastId);
      showToast.success(
        isCurrentlyActive
          ? "Question type unpublished!"
          : "Question type published!"
      );
      dispatch(fetchAllQuestionTypes());
    } catch (err) {
      console.error("Failed to update status", err);
      showToast.dismiss(toastId);
      showToast.error("Failed to update status.");
    }
  };

  // Pagination
  const totalPages = Math.ceil(allQuestionTypes.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedData = allQuestionTypes.slice(startIndex, startIndex + recordsPerPage);

  useEffect(() => {
    dispatch(fetchAllQuestionTypes());
  }, [dispatch]);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [allQuestionTypes.length]);

  // Fetch mediums when popup opens
  useEffect(() => {
    if (showDetailsPopup && boardId) {
      dispatch(fetchMediums(boardId));
    }
  }, [showDetailsPopup, boardId, dispatch]);

  // Fetch classes when medium is selected
  useEffect(() => {
    if (selectedMediumId && boardId) {
      dispatch(
        fetchClassesByBoardMedium({
          board_id: boardId,
          medium_id: selectedMediumId,
        })
      );
      setSelectedClassId("");
      setSelectedSubjectIds([]);
    }
  }, [selectedMediumId, boardId, dispatch]);

  // Fetch subjects and mapped subjects when class is selected
  useEffect(() => {
    if (selectedClassId) {
      // First reset the selected subjects
      setSelectedSubjectIds([]);

      // Fetch both subjects and mapped subjects
      dispatch(fetchLinkedSubjects(selectedClassId));

      if (selectedMediumId && selectedType && boardId) {
        dispatch(
          fetchMappedSubjects({
            board_id: boardId,
            medium_id: selectedMediumId,
            class_id: selectedClassId,
            type_id: selectedType.type_id,
          })
        );
      }
    }
  }, [selectedClassId, selectedMediumId, selectedType, boardId, dispatch]);

  // Pre-select already mapped subjects after they are loaded
  useEffect(() => {
    console.log("Mapped subjects received:", mappedSubjects);
    console.log("Mapped subjects loading:", mappedSubjectsLoading);
    console.log("Subjects loading:", subjectsLoading);

    if (!mappedSubjectsLoading && !subjectsLoading) {
      if (mappedSubjects && mappedSubjects.length > 0) {
        const mappedSubjectIds = mappedSubjects.map((item) => item.subject_id || item.id);
        console.log("Pre-selecting mapped subject IDs:", mappedSubjectIds);
        setSelectedSubjectIds(mappedSubjectIds);
      } else {
        console.log("No mapped subjects found, keeping selection empty");
      }
    }
  }, [mappedSubjects, mappedSubjectsLoading, subjectsLoading]);

  const handleViewDetails = (type) => {
    setSelectedType(type);
    setSelectedMediumId("");
    setSelectedClassId("");
    setSelectedSubjectIds([]);
    setShowDetailsPopup(true);
  };

  const handleSubjectCheckboxChange = (subjectId) => {
    setSelectedSubjectIds((prev) => {
      if (prev.includes(subjectId)) {
        return prev.filter((id) => id !== subjectId);
      } else {
        return [...prev, subjectId];
      }
    });
  };

  const handleSubmitLinking = async (e) => {
    e.preventDefault();

    // Console log as requested
    console.log({
      boardId,
      mediumId: selectedMediumId,
      classId: selectedClassId,
      subjectIds: selectedSubjectIds,
      questionTypeId: selectedType?.type_id,
    });

    const mappingData = {
      board_id: parseInt(boardId),
      medium_id: parseInt(selectedMediumId),
      class_id: parseInt(selectedClassId),
      type_id: selectedType.type_id,
      subject_ids: selectedSubjectIds.map((id) => parseInt(id)),
    };

    const toastId = showToast.loading("Mapping question type to subjects...");

    try {
      await dispatch(mapTypeToSubjects(mappingData)).unwrap();
      showToast.dismiss(toastId);
      showToast.success("Question type mapped successfully!");
      handleClosePopup();
    } catch (error) {
      console.error("Error mapping type:", error);
      showToast.dismiss(toastId);
      showToast.error("Failed to map question type to subjects.");
    }
  };

  const handleClosePopup = () => {
    setShowDetailsPopup(false);
    setSelectedType(null);
    setSelectedMediumId("");
    setSelectedClassId("");
    setSelectedSubjectIds([]);
  };

  // const handleDeleteQuestionType = async (typeId) => {
  //   const confirmDelete = window.confirm(
  //     "Are you sure you want to delete this question type?"
  //   );

  //   if (!confirmDelete) return;

  //   const toastId = showToast.loading("Deleting question type...");

  //   try {
  //     await dispatch(deleteQuestionType(typeId)).unwrap();

  //     showToast.dismiss(toastId);
  //     showToast.success("Question type deleted successfully!");

  //     dispatch(fetchAllQuestionTypes());
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     showToast.dismiss(toastId);
  //     showToast.error("Failed to delete question type.");
  //   }
  // };
  const handleDeleteQuestionType = async (typeId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this question type?"
    );

    if (!confirmDelete) return;

    const toastId = showToast.loading("Deleting question type...");

    try {
      const result = await dispatch(deleteQuestionType(typeId)).unwrap();

      showToast.dismiss(toastId);
      showToast.success(result.message || "Question type deleted successfully");

      dispatch(fetchAllQuestionTypes());
    } catch (error) {
      showToast.dismiss(toastId);
      showToast.error(error || "Failed to delete question type");
    }
  };

  const columns = [
    {
      header: "Name",
      accessor: "type_name",
    },
    {
      header: "Title",
      accessor: "type_title",
    },
    {
      header: "Status",
      accessor: "is_active",
      align: "center",
      render: (_, row) => {
        const isActive = row.is_active === 1;
        return (
          <span
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: "600",
              whiteSpace: "nowrap",
              color: isActive ? "#15803d" : "#dc2626",
              backgroundColor: isActive ? "#dcfce7" : "#fee2e2",
              border: `1px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessor: "type_id",
      align: "center",
      render: (value, row) => {
        const isActive = row.is_active === 1;
        return (
          <div
            style={{
              display: "flex",
              gap: "16px",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span
              onClick={() => handleViewDetails(row)}
              style={{
                fontSize: "20px",
                cursor: "pointer",
                display: "inline-block",
                transition: "transform 0.2s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.2)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
              title="Link to subjects"
            >
              🔗
            </span>
            <div
              onClick={() => handleStatusToggle(row.type_id, row.is_active)}
              title={isActive ? "Unpublish" : "Publish"}
              style={{
                width: "36px",
                height: "18px",
                borderRadius: "9px",
                backgroundColor: isActive ? "#22c55e" : "#d1d5db",
                position: "relative",
                cursor: "pointer",
                transition: "background-color 0.3s",
              }}
            >
              <div
                style={{
                  width: "14px",
                  height: "14px",
                  borderRadius: "50%",
                  backgroundColor: "white",
                  position: "absolute",
                  top: "2px",
                  left: isActive ? "20px" : "2px",
                  transition: "left 0.3s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }}
              />
            </div>
            <i
              className="fa fa-pencil mx-2"
              style={{ color: "blue", cursor: "pointer" }}
              title="Edit"
              onClick={() => handleEditClick(row)}
            ></i>
            <i
              className="fa fa-trash mx-2"
              style={{ color: "red", cursor: "pointer" }}
              title="Delete"
              onClick={() => handleDeleteQuestionType(row.type_id)}
            ></i>
          </div>
        );
      },
    },
  ];

  // Columns for the details popup table
  const detailColumns = [
    {
      header: "Type Name",
      accessor: "type_name",
    },
    {
      header: "Type Title",
      accessor: "type_title",
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: () => (
        <button
          onClick={() => setShowDetailsPopup(false)}
          style={{
            background: "#95a5a6",
            color: "white",
            border: "none",
            padding: "6px 12px",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: "500",
          }}
        >
          Close
        </button>
      ),
    },
  ];

  if (allQuestionTypesLoading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div
          style={{
            fontSize: "48px",
            marginBottom: "16px",
            animation: "spin 1s linear infinite",
          }}
        >
          ⏳
        </div>
        <p style={{ fontSize: "16px", color: "#6b7280" }}>
          Loading question types...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>❌</div>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#dc2626",
            marginBottom: "8px",
          }}
        >
          Error Loading Question Types
        </h3>
        <p style={{ fontSize: "14px", color: "#6b7280" }}>{error}</p>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />

      <div style={{ padding: "0", maxWidth: "100%", margin: "0 auto" }}>
        <style>
          {`
            .table-scroll-container::-webkit-scrollbar {
              width: 8px;
              height: 8px;
            }

            .table-scroll-container::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 4px;
            }

            .table-scroll-container::-webkit-scrollbar-thumb {
              background: #888;
              border-radius: 4px;
            }

            .table-scroll-container::-webkit-scrollbar-thumb:hover {
              background: #555;
            }
          `}
        </style>

        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Question Types
            </h2>
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Question types in the system ({allQuestionTypes.length})
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreateNew}
          >
            Create Category
          </button>
        </div>

        {/* TABLE */}
        {allQuestionTypes.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📝</div>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "8px",
              }}
            >
              No Question Types
            </h3>
            <p style={{ fontSize: "14px", color: "#6b7280", margin: 0 }}>
              There are no question types available.
            </p>
          </div>
        ) : (
          <>
            <div
              className="table-scroll-container"
              style={{
                maxHeight: "600px",
                overflowY: "auto",
                overflowX: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <Table
                columns={columns}
                data={paginatedData}
                showSerialNumber={true}
                serialNumberOffset={startIndex}
              />
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                  padding: "0 4px",
                }}
              >
                <span style={{ fontSize: "13px", color: "#6b7280" }}>
                  Showing {startIndex + 1}-{Math.min(startIndex + recordsPerPage, allQuestionTypes.length)} of {allQuestionTypes.length}
                </span>
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "13px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      background: currentPage === 1 ? "#f3f4f6" : "white",
                      color: currentPage === 1 ? "#9ca3af" : "#374151",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: "6px 10px",
                        fontSize: "13px",
                        border: page === currentPage ? "1px solid #3498db" : "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: page === currentPage ? "#3498db" : "white",
                        color: page === currentPage ? "white" : "#374151",
                        cursor: "pointer",
                        fontWeight: page === currentPage ? "600" : "400",
                      }}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "6px 12px",
                      fontSize: "13px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      background: currentPage === totalPages ? "#f3f4f6" : "white",
                      color: currentPage === totalPages ? "#9ca3af" : "#374151",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Add Category */}
        {showForm && (
          <Popup
            title={editingCategory ? "Edit Category" : "Add New Category"}
            onClose={handleFormCancel}
            maxWidth="400px"
            minHeight="400px"
          >
            <CategoryForm />
          </Popup>
        )}

        {/* Details Popup */}
        {showDetailsPopup && selectedType && (
          <Popup
            title={`Link Question Type - ${selectedType.type_name}`}
            onClose={handleClosePopup}
            maxWidth="900px"
          >
            <div style={{ padding: "10px" }}>
              <form onSubmit={handleSubmitLinking}>
                {/* Medium and Class Dropdowns - Display Flex */}
                <div style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
                  {/* Medium Dropdown */}
                  <div style={{ flex: 1 }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#495057",
                      }}
                    >
                      Medium <span style={{ color: "#e74c3c" }}>*</span>
                    </label>
                    <select
                      value={selectedMediumId}
                      onChange={(e) => setSelectedMediumId(e.target.value)}
                      disabled={mediumsLoading}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        height: "38px",
                      }}
                    >
                      <option value="">
                        {mediumsLoading ? "Loading..." : "-- Select Medium --"}
                      </option>
                      {mediums.map((medium) => (
                        <option key={medium.id} value={medium.id}>
                          {medium.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Class Dropdown */}
                  {selectedMediumId && (
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "6px",
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#495057",
                        }}
                      >
                        Class <span style={{ color: "#e74c3c" }}>*</span>
                      </label>
                      <select
                        value={selectedClassId}
                        onChange={(e) => setSelectedClassId(e.target.value)}
                        disabled={classesLoading}
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: "14px",
                          height: "38px",
                        }}
                      >
                        <option value="">
                          {classesLoading ? "Loading..." : "-- Select Class --"}
                        </option>
                        {classes.map((classItem) => (
                          <option key={classItem.class_id} value={classItem.class_id}>
                            {classItem.class_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Subject Checkboxes - Display Flex */}
                {selectedClassId && (
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "10px",
                        fontSize: "13px",
                        fontWeight: "500",
                        color: "#495057",
                      }}
                    >
                      Select Subjects <span style={{ color: "#e74c3c" }}>*</span>
                    </label>
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        padding: "15px",
                        background: "#f8f9fa",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                      }}
                      className="table-scroll-container"
                    >
                      {subjectsLoading || mappedSubjectsLoading ? (
                        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>
                          {subjectsLoading ? "Loading subjects..." : "Loading mapped subjects..."}
                        </p>
                      ) : subjects.length === 0 ? (
                        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>No subjects available for this class</p>
                      ) : (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                          {subjects.map((subject) => (
                            <div
                              key={subject.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 12px",
                                background: "white",
                                borderRadius: "6px",
                                border: selectedSubjectIds.includes(subject.id)
                                  ? "2px solid #3498db"
                                  : "1px solid #e5e7eb",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                minWidth: "200px",
                              }}
                              onClick={() => handleSubjectCheckboxChange(subject.id)}
                            >
                              <input
                                type="checkbox"
                                id={`subject-${subject.id}`}
                                checked={selectedSubjectIds.includes(subject.id)}
                                onChange={() => handleSubjectCheckboxChange(subject.id)}
                                style={{
                                  marginRight: "10px",
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <label
                                htmlFor={`subject-${subject.id}`}
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  cursor: "pointer",
                                  userSelect: "none",
                                  margin: 0,
                                }}
                              >
                                {subject.subject_name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedSubjectIds.length > 0 && (
                      <p style={{ marginTop: "8px", fontSize: "12px", color: "#3498db", fontWeight: "500" }}>
                        {selectedSubjectIds.length} subject(s) selected
                      </p>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", paddingTop: "15px", borderTop: "1px solid #e0e0e0" }}>
                  <button
                    type="button"
                    onClick={handleClosePopup}
                    style={{
                      background: "#95a5a6",
                      color: "white",
                      border: "none",
                      padding: "9px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    // disabled={!selectedMediumId || !selectedClassId || selectedSubjectIds.length === 0 || mappingTypeToSubjects}
                    disabled={!selectedMediumId || !selectedClassId || mappingTypeToSubjects}
                    style={{
                      background: selectedMediumId && selectedClassId && !mappingTypeToSubjects ? "#3498db" : "#95a5a6",
                      color: "white",
                      border: "none",
                      padding: "9px 24px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: "500",
                      cursor: selectedMediumId && selectedClassId && !mappingTypeToSubjects ? "pointer" : "not-allowed",
                    }}
                  >
                    {mappingTypeToSubjects ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </Popup>
        )}
      </div>
    </>
  );
};

export default QuestionTypes;
